import axios, { type AxiosError } from 'axios';
import { config, TOKEN_STORAGE_KEY } from '../config';
import type {
  CreateEmployeeResponse,
  EmployeeMutationResponse,
  EmployeesListResponse,
  LoginResponse,
} from '../types/auth';
import type {
  CargoCompanyRow,
  CargoCompanyStats,
  CargoEmployeeRow,
  CargoInvoiceDetail,
  CargoInvoiceRow,
  CargoLedgerEntry,
  CargoOverview,
  Pagination,
  SystemStatus,
  AuditLogsResponse,
  KybDetailResponse,
  MerchantDetailResponse,
  MerchantDocumentsResponse,
  MerchantProfilePatch,
  MerchantResponse,
  MerchantsResponse,
  PayoutsResponse,
  TransactionsResponse,
  UiMerchantStatus,
  WalletChangelogResponse,
} from './types';
import { mapUiToApi } from '../utils/merchantStatus';

export const apiClient = axios.create({
  baseURL: config.apiBaseUrl,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((req) => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return req;
});

apiClient.interceptors.response.use(
  (res) => res,
  (err) => {
    if (axios.isAxiosError(err) && err.response?.status === 401) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem('onyx_employee');
      window.location.reload();
    }
    return Promise.reject(err);
  },
);

export function getApiErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const ax = err as AxiosError<{ error?: string }>;
    return ax.response?.data?.error || ax.message || 'Request failed';
  }
  return err instanceof Error ? err.message : 'Request failed';
}

export const api = {
  login: (email: string, password: string) =>
    apiClient.post<LoginResponse>('/v1/auth/login', { email, password }).then((r) => r.data),

  listEmployees: () =>
    apiClient.get<EmployeesListResponse>('/v1/auth/employees').then((r) => r.data),

  createEmployee: (body: { name: string; email: string; password: string; role: string }) =>
    apiClient.post<CreateEmployeeResponse>('/v1/auth/employees', body).then((r) => r.data),

  patchEmployeeRole: (employeeId: string, role: string) =>
    apiClient
      .patch<EmployeeMutationResponse>(`/v1/auth/employees/${employeeId}/role`, { role })
      .then((r) => r.data),

  deleteEmployee: (employeeId: string) =>
    apiClient.delete<{ ok: boolean; error?: string }>(`/v1/auth/employees/${employeeId}`).then((r) => r.data),

  health: () => apiClient.get('/health').then((r) => r.data),

  systemStatus: () =>
    apiClient.get<{ ok: boolean; status: SystemStatus }>('/v1/admin/system/status').then((r) => r.data),

  listMerchants: (limit = 500) =>
    apiClient.get<MerchantsResponse>('/v1/merchants', { params: { limit } }).then((r) => r.data),

  listTransactions: (limit = 100, merchantId?: string) => {
    const params: Record<string, string | number> = { limit };
    if (merchantId) params.merchant_id = merchantId;
    return apiClient.get<TransactionsResponse>('/v1/transactions', { params }).then((r) => r.data);
  },

  getMerchant: (merchantId: string) =>
    apiClient.get<MerchantDetailResponse>(`/v1/merchants/${merchantId}`).then((r) => r.data),

  getMerchantDocuments: (merchantId: string) =>
    apiClient.get<MerchantDocumentsResponse>(`/v1/merchants/${merchantId}/documents`).then((r) => r.data),

  listPayoutRequests: () => apiClient.get<PayoutsResponse>('/v1/payout-requests').then((r) => r.data),

  executePayout: (payoutId: string) =>
    apiClient.post<{ ok: boolean; error?: string }>(`/v1/payout-requests/${payoutId}/execute`).then((r) => r.data),

  patchMerchantStatus: (merchantId: string, status: UiMerchantStatus) =>
    apiClient
      .patch<MerchantResponse>(`/v1/merchants/${merchantId}/status`, { status: mapUiToApi(status) })
      .then((r) => r.data),

  patchMerchantFees: (merchantId: string, feePercent: number) =>
    apiClient
      .patch<MerchantResponse>(`/v1/merchants/${merchantId}/fees`, { fee_percent: feePercent })
      .then((r) => r.data),

  patchMerchantProfile: (merchantId: string, body: MerchantProfilePatch) =>
    apiClient
      .patch<MerchantResponse>(`/v1/merchants/${merchantId}/profile`, body)
      .then((r) => r.data),

  patchMerchantSettlementWallet: (
    merchantId: string,
    solana_wallet: string,
    change_reason: string,
    staff_password: string,
  ) =>
    apiClient
      .patch<MerchantResponse & { merchant_vault?: string }>(`/v1/merchants/${merchantId}/settlement-wallet`, {
        solana_wallet,
        change_reason,
        staff_password,
      })
      .then((r) => r.data),

  getMerchantWalletChangelog: (merchantId: string, limit = 100) =>
    apiClient
      .get<WalletChangelogResponse>(`/v1/merchants/${merchantId}/wallet-changelog`, {
        params: { limit },
      })
      .then((r) => r.data),

  listAuditLogs: (page = 1, limit = 50, action?: string) =>
    apiClient
      .get<AuditLogsResponse>('/v1/audit-logs', { params: { page, limit, action } })
      .then((r) => r.data),

  exportTransactionsCsv: async (params?: { merchant_id?: string; limit?: number; from?: string; to?: string }) => {
    const res = await apiClient.get<Blob>('/v1/transactions/export.csv', {
      params,
      responseType: 'blob',
    });
    return res.data;
  },

  getMerchantKyb: (merchantId: string) =>
    apiClient.get<KybDetailResponse>(`/v1/merchants/${merchantId}/kyb`).then((r) => r.data),

  patchMerchantKyb: (merchantId: string, action: 'approve' | 'reject') =>
    apiClient
      .patch<MerchantResponse>(`/v1/merchants/${merchantId}/kyb`, { action })
      .then((r) => r.data),

  cargoOverview: () =>
    apiClient.get<{ ok: boolean; stats: CargoOverview }>('/v1/admin/cargo/overview').then((r) => r.data),

  listCargoCompanies: (params?: { page?: number; limit?: number; status?: string }) =>
    apiClient
      .get<{ ok: boolean; companies: CargoCompanyRow[]; pagination: Pagination }>(
        '/v1/admin/cargo/companies',
        { params },
      )
      .then((r) => r.data),

  createCargoCompany: (body: {
    company_name: string;
    email: string;
    password: string;
    full_name?: string;
    tron_payout_wallet?: string;
  }) =>
    apiClient.post('/v1/admin/cargo/companies', body).then((r) => r.data),

  getCargoCompany: (id: string) =>
    apiClient
      .get<{
        ok: boolean;
        company: CargoCompanyRow;
        employees: CargoEmployeeRow[];
        stats: CargoCompanyStats;
      }>(`/v1/admin/cargo/companies/${id}`)
      .then((r) => r.data),

  patchCargoCompany: (
    id: string,
    body: {
      name?: string;
      status?: 'pending' | 'active' | 'suspended';
      internal_notes?: string | null;
      tron_payout_wallet?: string | null;
    },
  ) =>
    apiClient.patch<{ ok: boolean; company: CargoCompanyRow }>(`/v1/admin/cargo/companies/${id}`, body).then((r) => r.data),

  listCargoInvoices: (params?: {
    page?: number;
    limit?: number;
    company_id?: string;
    status?: string;
    split_status?: string;
    q?: string;
  }) =>
    apiClient
      .get<{ ok: boolean; invoices: CargoInvoiceRow[]; pagination: Pagination }>(
        '/v1/admin/cargo/invoices',
        { params },
      )
      .then((r) => r.data),

  getCargoInvoice: (hashId: string) =>
    apiClient
      .get<{ ok: boolean; invoice: CargoInvoiceDetail; ledger: CargoLedgerEntry[] }>(
        `/v1/admin/cargo/invoices/${hashId}`,
      )
      .then((r) => r.data),

  exportCargoInvoicesCsv: async (params?: Record<string, string | number | undefined>) => {
    const res = await apiClient.get<Blob>('/v1/admin/cargo/invoices/export.csv', {
      params,
      responseType: 'blob',
    });
    return res.data;
  },

  retryCargoInvoiceSplit: (hashId: string) =>
    apiClient
      .post<{ ok: boolean; result: { ok: boolean; splitStatus: string; error?: string } }>(
        `/v1/admin/cargo/invoices/${hashId}/retry-split`,
        {},
      )
      .then((r) => r.data),

  mockVerifyCargoInvoice: (hashId: string, tx_hash?: string) =>
    apiClient
      .post(`/v1/admin/cargo/invoices/${hashId}/mock-verify`, { tx_hash })
      .then((r) => r.data),

  patchCargoEmployee: (
    companyId: string,
    memberId: string,
    body: { status?: 'active' | 'disabled'; role?: 'admin' | 'manager'; full_name?: string | null },
  ) =>
    apiClient
      .patch(`/v1/admin/cargo/companies/${companyId}/employees/${memberId}`, body)
      .then((r) => r.data),

  resetCargoEmployeePassword: (companyId: string, memberId: string, new_password: string) =>
    apiClient
      .patch(`/v1/admin/cargo/companies/${companyId}/employees/${memberId}/password`, { new_password })
      .then((r) => r.data),

  getCargoCommissionSettings: () =>
    apiClient
      .get<{ ok: true } & import('./types').CargoCommissionSettings>(
        '/v1/admin/cargo/commission-settings',
      )
      .then((r) => r.data),

  patchCargoDefaultCommission: (commission_bps: number) =>
    apiClient
      .patch<{ ok: true; default_commission_bps: number }>(
        '/v1/admin/cargo/commission-settings/default',
        { commission_bps },
      )
      .then((r) => r.data),

  patchCargoCompanyCommission: (companyId: string, commission_bps: number | null) =>
    apiClient
      .patch<{
        ok: true;
        company_id: string;
        commission_bps: number | null;
        effective_commission_bps: number;
      }>(`/v1/admin/cargo/companies/${companyId}/commission`, { commission_bps })
      .then((r) => r.data),
};
